import type { IntlShape } from 'react-intl';

import type {
  FilterValues,
  SetValueProps,
} from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import type { RelativeAbsolutePeriodValue } from '@src/components/RelativeAbsolutePeriodFilter';
import { translateString } from '@src/util/intl/translateString';

import {
  buildInitScheduleFilterValues,
  buildDefaultScheduleChartFilters,
  buildScheduleFilterSetValueProps,
  DEFAULT_DATE_RANGE,
  DEFAULT_SELECTED_SCHEDULES,
  formatGroupByTag,
  filterValuesToScheduleFilters,
  formatSchedulesForTag,
  hasSelectedSchedules,
  isTruthyRelativeMode,
  mergeFilterSetValueProps,
  normalizeStoredScheduleFilters,
  SCHEDULE_GROUP_BY_DEFAULT,
  SCHEDULES_REPORT_MAX_SPAN,
  SCHEDULE_RELATIVE_DEFAULT,
  SCHEDULES_DASHBOARD_FILTER_FIELDS,
  scheduleFiltersToStoragePayload,
  scheduleSelectionAffectsApiFetch,
  scheduleFiltersToPeriodValue,
  periodValueToScheduleFilterProps,
  areScheduleSelectionsEqual,
  getScheduleSelectionKey,
  getScheduleIdsForApi,
  type ScheduleChartFilters,
  type ScheduleRelativeFilter,
} from '../SchedulesDashboard/schedulesFilterConfig';
import type { SelectedSchedulesState } from '../SchedulesDashboard/types';

export const CHECKPOINT_ROUTES_DASHBOARD_FILTER_FIELDS = {
  RELATIVE: 'checkpointRoutes.relative',
  DATE_RANGE: 'checkpointRoutes.dateRange',
  GROUP_BY: 'checkpointRoutes.groupBy',
  SCHEDULES: 'checkpointRoutes.selected',
  COST_CENTERS: 'checkpointRoutes.costCenterIds',
} as const;

export const CHECKPOINT_ROUTES_DASHBOARD_FILTERS_STORAGE_KEY = 'checkpointRoutesDashboardFilters';

export type CheckpointRoutesChartFilters = ScheduleChartFilters;
export type CheckpointRoutesRelativeFilter = ScheduleRelativeFilter;

export {
  DEFAULT_DATE_RANGE,
  DEFAULT_SELECTED_SCHEDULES,
  SCHEDULE_GROUP_BY_DEFAULT,
  SCHEDULES_REPORT_MAX_SPAN,
  SCHEDULE_RELATIVE_DEFAULT,
  formatGroupByTag,
  formatSchedulesForTag,
  hasSelectedSchedules,
  isTruthyRelativeMode,
  mergeFilterSetValueProps,
  normalizeStoredScheduleFilters as normalizeStoredCheckpointRoutesFilters,
  scheduleFiltersToStoragePayload as checkpointRoutesFiltersToStoragePayload,
  scheduleSelectionAffectsApiFetch,
  areScheduleSelectionsEqual,
  getScheduleSelectionKey,
  getScheduleIdsForApi,
};

const ROUTES_FILTER_LABEL_ID = 'dashboards.chart.form.scheduleCompliance.groupBy.routes';

const toScheduleFilterValues = (values: Record<string, { value?: unknown }>) => ({
  [SCHEDULES_DASHBOARD_FILTER_FIELDS.RELATIVE]: values[
    CHECKPOINT_ROUTES_DASHBOARD_FILTER_FIELDS.RELATIVE
  ],
  [SCHEDULES_DASHBOARD_FILTER_FIELDS.DATE_RANGE]: values[
    CHECKPOINT_ROUTES_DASHBOARD_FILTER_FIELDS.DATE_RANGE
  ],
  [SCHEDULES_DASHBOARD_FILTER_FIELDS.GROUP_BY]: values[
    CHECKPOINT_ROUTES_DASHBOARD_FILTER_FIELDS.GROUP_BY
  ],
  [SCHEDULES_DASHBOARD_FILTER_FIELDS.SCHEDULES]: values[
    CHECKPOINT_ROUTES_DASHBOARD_FILTER_FIELDS.SCHEDULES
  ],
  [SCHEDULES_DASHBOARD_FILTER_FIELDS.COST_CENTERS]: values[
    CHECKPOINT_ROUTES_DASHBOARD_FILTER_FIELDS.COST_CENTERS
  ],
});

export const filterValuesToCheckpointRoutesFilters = (
  values: Record<string, { value?: unknown }>,
): CheckpointRoutesChartFilters => filterValuesToScheduleFilters(toScheduleFilterValues(values));

export const filterValuesToCheckpointRoutesFiltersUi = (
  values: Record<string, { value?: unknown }>,
  fallback: CheckpointRoutesChartFilters,
): CheckpointRoutesChartFilters => {
  const hasCheckpointRouteValues = Object.values(CHECKPOINT_ROUTES_DASHBOARD_FILTER_FIELDS).some(
    key => Object.prototype.hasOwnProperty.call(values ?? {}, key),
  );
  return hasCheckpointRouteValues ? filterValuesToCheckpointRoutesFilters(values) : fallback;
};

export const checkpointRoutesFiltersToPeriodValue = (
  chartFilters: CheckpointRoutesChartFilters,
): RelativeAbsolutePeriodValue => scheduleFiltersToPeriodValue(chartFilters);

export const periodValueToCheckpointRoutesFilterProps = (
  period: RelativeAbsolutePeriodValue,
  intl: IntlShape,
): SetValueProps[] =>
  periodValueToScheduleFilterProps(period, intl).map(props => ({
    ...props,
    key:
      props.key === SCHEDULES_DASHBOARD_FILTER_FIELDS.RELATIVE
        ? CHECKPOINT_ROUTES_DASHBOARD_FILTER_FIELDS.RELATIVE
        : CHECKPOINT_ROUTES_DASHBOARD_FILTER_FIELDS.DATE_RANGE,
  }));

export const buildInitCheckpointRoutesFilterValues = (
  chartFilters: CheckpointRoutesChartFilters,
  intl: IntlShape,
): FilterValues => {
  const base = buildInitScheduleFilterValues(chartFilters, intl);

  return {
    [CHECKPOINT_ROUTES_DASHBOARD_FILTER_FIELDS.RELATIVE]:
      base[SCHEDULES_DASHBOARD_FILTER_FIELDS.RELATIVE],
    [CHECKPOINT_ROUTES_DASHBOARD_FILTER_FIELDS.DATE_RANGE]:
      base[SCHEDULES_DASHBOARD_FILTER_FIELDS.DATE_RANGE],
    [CHECKPOINT_ROUTES_DASHBOARD_FILTER_FIELDS.GROUP_BY]:
      base[SCHEDULES_DASHBOARD_FILTER_FIELDS.GROUP_BY],
    [CHECKPOINT_ROUTES_DASHBOARD_FILTER_FIELDS.SCHEDULES]: {
      ...base[SCHEDULES_DASHBOARD_FILTER_FIELDS.SCHEDULES],
      label: translateString(ROUTES_FILTER_LABEL_ID),
    },
    [CHECKPOINT_ROUTES_DASHBOARD_FILTER_FIELDS.COST_CENTERS]:
      base[SCHEDULES_DASHBOARD_FILTER_FIELDS.COST_CENTERS],
  };
};

export const buildCheckpointRoutesFilterSetValueProps = (
  key: string,
  value: unknown,
  intl: IntlShape,
): SetValueProps => {
  if (key === CHECKPOINT_ROUTES_DASHBOARD_FILTER_FIELDS.SCHEDULES) {
    const schedules = value as SelectedSchedulesState | null;
    return {
      key,
      value: (hasSelectedSchedules(schedules)
        ? schedules
        : null) as unknown as SetValueProps['value'],
      label: translateString(ROUTES_FILTER_LABEL_ID),
      valueTranslated: formatSchedulesForTag(schedules, intl),
      valueType: 'array',
      clearValue: null,
    };
  }

  const scheduleKey = {
    [CHECKPOINT_ROUTES_DASHBOARD_FILTER_FIELDS.RELATIVE]: SCHEDULES_DASHBOARD_FILTER_FIELDS.RELATIVE,
    [CHECKPOINT_ROUTES_DASHBOARD_FILTER_FIELDS.DATE_RANGE]: SCHEDULES_DASHBOARD_FILTER_FIELDS.DATE_RANGE,
    [CHECKPOINT_ROUTES_DASHBOARD_FILTER_FIELDS.GROUP_BY]: SCHEDULES_DASHBOARD_FILTER_FIELDS.GROUP_BY,
    [CHECKPOINT_ROUTES_DASHBOARD_FILTER_FIELDS.COST_CENTERS]:
      SCHEDULES_DASHBOARD_FILTER_FIELDS.COST_CENTERS,
  }[key];
  if (!scheduleKey) {
    throw new Error(`Unsupported checkpoint routes filter key: ${key}`);
  }
  const props = buildScheduleFilterSetValueProps(scheduleKey, value, intl);
  return { ...props, key };
};

export const buildDefaultCheckpointRoutesChartFilters = (): CheckpointRoutesChartFilters => ({
  ...buildDefaultScheduleChartFilters(),
});
